import { ProductTypeNames, ProductTypeStrings } from '@/constants/product';
import { BonusConflict, BonusConflictReason, BonusType, BonusTypeName } from '@/constants/bonus';
import { paymentProviderNameById } from '@/constants/paymentProvider';
import { formatCurrency } from '@/modules/bets/utils/formatters';
import { getFormattedCurrencyAmount } from '@/page-components/utils/functions';
import i18next from 'i18next';

interface BonusConflictInterface {
  message: string;
  translatePlaceholders?: { [key: string]: any };
}
interface BonusInterface {
  id: number | string;
  name?: string;
  product?: string[];
  payment_method?: string[];
  payment_method_raw?: number[];
  amount?: number;
  currency?: string;
  amount_display?: string; // what is shown in the frontend (amount + currency) or (free spins) or (tokens x value)
  end_date?: string;
  show_date?: boolean;
  terms_url?: string;
  cta_text?: string;
  bonus_conflicts?: BonusConflictInterface[];
  bonus_type?: string;
  bonus_type_id?: number;
  description?: string;
  image?: string;
  icon?: string;
  deposit?: {
    min?: number;
    max?: number;
  };
}

class Bonus implements BonusInterface {
  id: number | string = 0;
  rawData?: any;
  name?: string;
  product?: string[];
  payment_method?: string[];
  payment_method_raw?: number[];
  amount?: number;
  currency?: string;
  amount_display?: string; // what is shown in the frontend (amount + currency) or (free spins) or (tokens x value)
  end_date?: string;
  show_date?: boolean;
  terms_url?: string;
  cta_text?: string;
  bonus_conflicts?: BonusConflictInterface[];
  bonus_type?: string;
  bonus_type_id?: number;
  description?: string;
  image?: string;
  icon?: string;
  deposit?: {
    min?: number;
    max?: number;
  };
  reward_id?: string;
  constructor(bonus: any, award?: any) {
    if (bonus && bonus.bonus_available_id !== undefined) {
      this.initFromBonusAvailable(bonus, award);
    } else if (bonus && bonus.reward !== undefined) {
      this.initFromRewardBundle(bonus, award);
    } else {
      this.isNoBonusType();
    }
  }

  haveInfo(): boolean {
    return this.id !== undefined;
  }

  /**
   * Set bonus to no bonus type
   * used on deposit page when no bonus is selected
   * @returns void
   */
  isNoBonusType(): void {
    this.id = 0;
    this.rawData = {};
    this.name = 'BONUS_PICKER.WITHOUT_BONUS';
    this.product = [];
    this.payment_method = [];
    this.payment_method_raw = [];
    this.amount = 0;
    this.currency = '';
    this.amount_display = 'BONUS_PICKER.DEPOSIT_WITHOUT_BONUS';
    this.end_date = '';
    this.show_date = false;
    this.terms_url = '';
    this.cta_text = 'BONUS_PICKER.NO_BONUS';
    this.bonus_conflicts = [];
    this.bonus_type = 'Deposit';
    this.bonus_type_id = 1;
    this.description = '';
    this.image = '';
    this.icon = '';
    this.deposit = {
      min: 0,
      max: 0,
    };
  }

  initFromRewardBundle(bonus: any, award?: any) {
    // get language from config
    const language = i18next.language;
    this.reward_id = bonus.reward.id;
    this.id = /*bonus.prizes[0].reward_metadata?.bonus_available_id ??*/ -1;
    this.name = bonus.reward.display_name?.text ?? bonus.reward.display_name;
    this.rawData = bonus;

    this.amount = bonus.prizes[0].value; // value is not in cents;
    this.currency =
      bonus.prizes[0].currency?.art_bundle?.text_display_name?.text?.[language] ?? window.config.defaultCurrency;
    this.amount_display = ''; // calculated later
    // this.end_date = bonus.bonus_campaign_end;
    this.show_date = false;
    this.terms_url = bonus.prizes[0].reward_metadata?.terms_url ?? '';
    this.cta_text = bonus.prizes[0].reward_metadata?.cta_text ?? 'GET PRIZE';
    this.bonus_conflicts = [];
    this.bonus_type = bonus.prizes[0].reward_type;
    this.bonus_type_id = BonusType.wheelToken;
    this.description = bonus.prizes[0].meta?.public_description?.text?.[language] ?? '';
    this.image = bonus.reward?.art_bundle?.[0]?.small_square_ar11_keycenter ?? '';
    this.icon = bonus.reward?.art_bundle?.[0]?.small_square_ar11_keycenter ?? '';

    this.deposit = {
      min: Number(award?.bonusMin ? award.bonusMin / 100 : (bonus.prizes[0].min_deposit_amount_req?.value ?? 0)), // value is not in cents / 100,
      max: Number(
        award?.bonusMax
          ? award.bonusMax / 100
          : (bonus.prizes[0].max_deposit_amount_req?.value ?? Number.MAX_SAFE_INTEGER),
      ), // value is not in cents / 100,
    };

    this.product = bonus.reward.intended_for_products.map((product: any) => {
      return { id: ProductTypeStrings[product], name: ProductTypeNames[product] };
    });

    if (bonus.reward.eligible_payment_methods !== undefined) {
      this.payment_method_raw = bonus.reward.eligible_payment_methods;
      this.payment_method = bonus.reward.eligible_payment_methods.map((payment_method: any) => {
        return paymentProviderNameById[payment_method] ?? '';
      });
    }

    // @ts-ignore
    this.bonus_type = BonusTypeName[this.bonus_type_id] ?? '';

    this.amount_display = this.calculateAmountDisplay(award, this.deposit.max);
  }

  initFromBonusAvailable(bonus: any, award?: any) {
    this.id = bonus.bonus_available_id;
    this.name = bonus.bonus_available_name;
    this.rawData = bonus;

    this.amount = bonus.bonus_available_max; // value is not in cents;
    this.currency = bonus.currency_code ?? window.config.defaultCurrency;
    this.amount_display = ''; // calculated later
    this.end_date = bonus.bonus_campaign_end;
    this.show_date = true;
    this.terms_url = bonus.terms_url ?? '';
    this.cta_text = bonus.cta_text ?? 'GET BONUS';
    this.bonus_conflicts = [];
    this.bonus_type = bonus.bonus_available_type;
    this.bonus_type_id = bonus.bonus_available_type;
    this.description = bonus.description ?? '';
    this.image = bonus.deposit_wizard_image ?? '';
    this.icon = bonus.icon_image ?? '';

    this.deposit = {
      min: Number(award?.bonusMin ? award.bonusMin / 100 : bonus.bonus_available_min), // value is not in cents / 100,
      max: Number(award?.bonusMax ? award.bonusMax / 100 : bonus.bonus_available_max), // value is not in cents / 100,
    };

    this.product = bonus.eligible_products.map((product: any) => {
      return { id: product, name: ProductTypeNames[product] };
    });

    if (bonus.bonus_conflicts !== undefined) {
      // check if it's an object
      if (typeof bonus.bonus_conflicts === 'object' && bonus.bonus_conflicts !== null) { // tODO: fix this condition with null check; null == object
        this.bonus_conflicts = Object.keys(bonus.bonus_conflicts).map((index: any) => {
          return BonusConflictReason[index]
            ? { message: BonusConflictReason[index] }
            : { message: BonusConflictReason[BonusConflict.unknown] };
        });
      }
    }

    if (bonus.eligible_payment_methods !== undefined) {
      this.payment_method_raw = bonus.eligible_payment_methods;
      this.payment_method = bonus.eligible_payment_methods.map((payment_method: any) => {
        return paymentProviderNameById[payment_method] ?? '';
      });
    }

    // @ts-ignore
    this.bonus_type = BonusTypeName[this.bonus_type_id] ?? '';

    this.amount_display = this.calculateAmountDisplay(award, this.deposit.max);
  }

  calculateAmountDisplay(award?: any, depositValue?: any): string {
    if (depositValue) {
      // @ts-ignore
      if (this?.deposit?.min > depositValue) {
        return 'No Bonus';
      }
    }
    if (this.id === 0 || this.id === -1) {
      return this.amount_display ?? '';
    }
    let amountToDisplay: string = '';

    const bonus = this.rawData;
    switch (this.bonus_type_id) {
      case BonusType.freeBetLotto:
      case BonusType.freeBetSport:
        if (award) {
          amountToDisplay = `${award.tokenCount} x ${award.awardAmount / 100} <small>${formatCurrency(this.currency)}</small>`;
        } else {
          amountToDisplay = `${bonus.bonus_type_specific.tokenCount} x 
          ${getFormattedCurrencyAmount({
            locale: '',
            amount: (bonus.bonus_type_specific.tokenValue / 100)!,
            currency: this.currency!,
            digits: '0',
          })}`;
        }
        break;
      case BonusType.freeSpin:
        amountToDisplay = `${award ? award.awardAmount : bonus.bonus_type_specific?.fs_count} <small>${formatCurrency('FS')}</small>`;
        break;
      case BonusType.freeSpinTimer:
        // unlimited sign / 5 signs
        amountToDisplay = `${award ? award.awardAmount : bonus.bonus_type_specific?.fs_time} <small>${formatCurrency('sec')}</small>`;
        break;
      case BonusType.freeRound:
        amountToDisplay = `${award ? award.awardAmount/100 : bonus.bonus_type_specific?.fs_amount/100} <small>${formatCurrency(this.currency)}</small>`;
        break;
      case BonusType.scalar:
      case BonusType.percentage:
        amountToDisplay =
          // (award ? award.awardAmount / 100 : this.amount) + ' <small>' + formatCurrency(this.currency) + '</small>';
          getFormattedCurrencyAmount({
            locale: '',
            amount: (award ? award.awardAmount / 100 : this.amount)!,
            currency: this.currency!,
            digits: 0,
          });
        break;
      default:
        // do nothing
        break;
    }
    return amountToDisplay;
  }

  getBonusId(): number | string | null {
    return this.id !== -1 ? this.id : null;
  }

  isReward(): boolean {
    return !!this.reward_id;
  }

  getRewardId(): string | null {
    return this.reward_id ?? null;
  }

  getId(): number | string | null {
    return this.id ?? null;
  }

  getBonusName(): string {
    return this.name ?? '';
  }

  getBonusProducts(): string[] {
    return this.product ?? [];
  }

  getBonusAmount(): number | undefined {
    return this.amount;
  }

  getBonusCurrency(): string {
    return this.currency ?? '';
  }

  getBonusAmountDisplay(award?: any, depositValue?: any): string {
    return this.calculateAmountDisplay(award, depositValue);
  }

  getBonusEndDate(): string {
    return this.end_date ?? '';
  }

  getBonusShowDate(): boolean {
    return !!this.show_date;
  }

  getBonusTermsUrl(): string {
    return this.terms_url ?? '';
  }

  getBonusCtaText(): string {
    return this.cta_text ?? '';
  }

  getBonusConflicts(): BonusConflictInterface[] {
    return this.bonus_conflicts ?? [];
  }

  haveConflicts(): boolean {
    // @ts-ignore
    return this.bonus_conflicts?.length > 0;
  }
  getBonusType(): string {
    return this.bonus_type ?? '';
  }

  getBonusTypeId(): number {
    return this.bonus_type_id ?? 0;
  }

  getBonusDescription(): string {
    return this.description ?? '';
  }

  getBonusDepositMin(): number {
    return this.deposit?.min ?? 0;
  }

  getBonusDepositMax(): number {
    return this.deposit?.max ?? 0;
  }

  getBonusDeposit(): { min: number | undefined; max: number | undefined } {
    return { min: this.getBonusDepositMin(), max: this.getBonusDepositMax() };
  }

  getBonusPaymentMethods(): string[] {
    return this.payment_method ?? [];
  }

  getBonusPaymentMethodsRaw(): number[] {
    return this.payment_method_raw ?? [];
  }

  getBonusPaymentMethodsDisplay(): string {
    return this.payment_method ? this.payment_method.join(', ') : '';
  }

  getBonusImage(): string {
    return this.image ?? '';
  }

  getBonusIcon(): string {
    return this.icon ?? '';
  }
}

export default Bonus;
